import {
  canonicalJsonStringify,
  parseShiftEditorDraft,
  type JsonValue,
  type ShiftEditorDraft,
} from '@der-maschinfuehrer/contracts';

import {
  AppDraftConflictError,
  type AppDraftSave,
  type AppDraftVersion,
  type AppStorage,
} from '../storage/app-storage';

export type AppDraftRecord = NonNullable<Awaited<ReturnType<AppStorage['getDraft']>>>;

export type DraftAutosaveStorage = Pick<AppStorage, 'autosaveDraft' | 'getDraft'>;

export type ShiftDraftAutosaveStatus =
  'clean' | 'dirty' | 'saving' | 'saved' | 'error' | 'conflict';

export interface ShiftDraftAutosaveSnapshot {
  readonly status: ShiftDraftAutosaveStatus;
  readonly editGeneration: number;
  readonly persistedGeneration: number;
  readonly hasPendingChanges: boolean;
  readonly record: AppDraftRecord | null;
  readonly error: unknown | null;
  readonly conflict: AppDraftConflictError | null;
}

export interface ShiftDraftAutosaveControllerOptions {
  readonly storage: DraftAutosaveStorage;
  readonly draftKey: string;
  readonly initialRecord?: AppDraftRecord | null;
  readonly debounceMs?: number;
}

export interface LoadedStoredShiftDraft {
  readonly draft: ShiftEditorDraft;
  readonly record: AppDraftRecord;
}

export class ShiftDraftAutosaveDisposedError extends Error {
  public constructor() {
    super('The shift draft autosave controller has been disposed.');
    this.name = new.target.name;
  }
}

export class ShiftDraftValueUnavailableError extends Error {
  public constructor() {
    super('No shift editor draft is available to save.');
    this.name = new.target.name;
  }
}

export class ShiftDraftAtomicWriteInProgressError extends Error {
  public constructor() {
    super('An atomic shift-draft mutation is already in progress.');
    this.name = new.target.name;
  }
}

export class StoredShiftDraftPayloadError extends Error {
  public constructor(message: string, cause?: unknown) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = new.target.name;
  }
}

/**
 * Serial, CAS-aware autosave for one shift-editor draft.
 *
 * The controller deliberately owns only persistence state. The editor remains
 * the source of truth for form state and explicitly supplies every new draft
 * generation through setDraft().
 */
export class ShiftDraftAutosaveController {
  readonly #storage: DraftAutosaveStorage;
  readonly #draftKey: string;
  readonly #debounceMs: number;
  readonly #listeners = new Set<() => void>();

  #latestDraft: ShiftEditorDraft | null = null;
  #expectedVersion: AppDraftVersion | null = null;
  #record: AppDraftRecord | null = null;
  #editGeneration = 0;
  #persistedGeneration = 0;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #inFlight: Promise<void> | null = null;
  #atomicWrite: Promise<void> | null = null;
  #disposed = false;
  #snapshot: ShiftDraftAutosaveSnapshot;

  public constructor(options: ShiftDraftAutosaveControllerOptions) {
    if (!Number.isFinite(options.debounceMs ?? 300) || (options.debounceMs ?? 300) < 0) {
      throw new RangeError('debounceMs must be a non-negative finite number.');
    }
    if (options.draftKey.trim() === '') {
      throw new TypeError('draftKey must not be empty.');
    }

    this.#storage = options.storage;
    this.#draftKey = options.draftKey;
    this.#debounceMs = options.debounceMs ?? 300;

    const initialRecord = options.initialRecord ?? null;
    if (initialRecord !== null) {
      const loaded = parseStoredRecord(initialRecord, this.#draftKey);
      this.#latestDraft = loaded.draft;
      this.#expectedVersion = versionOf(loaded.record);
      this.#record = loaded.record;
    }

    this.#snapshot = this.#makeSnapshot('clean', null, null);
  }

  public get draftKey(): string {
    return this.#draftKey;
  }

  public getSnapshot = (): ShiftDraftAutosaveSnapshot => this.#snapshot;

  public subscribe = (listener: () => void): (() => void) => {
    this.#assertActive();
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  public setDraft(draft: ShiftEditorDraft): void {
    this.#assertActive();
    if (this.#atomicWrite !== null) throw new ShiftDraftAtomicWriteInProgressError();
    this.#latestDraft = parseShiftEditorDraft(draft);
    this.#editGeneration += 1;

    if (this.#snapshot.status === 'conflict') {
      this.#publish('conflict', this.#snapshot.error, this.#snapshot.conflict);
      return;
    }

    this.#publish('dirty', null, null);
    if (this.#inFlight === null) {
      this.#scheduleDebouncedWrite();
    }
  }

  /** Persist every generation known when the controller becomes stable. */
  public async saveNow(): Promise<AppDraftRecord> {
    this.#assertActive();
    this.#cancelTimer();

    for (;;) {
      this.#assertActive();
      const atomicWrite = this.#atomicWrite;
      if (atomicWrite !== null) {
        await atomicWrite;
        continue;
      }
      if (this.#snapshot.conflict !== null) {
        throw this.#snapshot.conflict;
      }
      if (this.#snapshot.status === 'error') {
        throw this.#snapshot.error;
      }
      if (this.#persistedGeneration >= this.#editGeneration) {
        if (this.#record === null) {
          throw new ShiftDraftValueUnavailableError();
        }
        return this.#record;
      }
      if (this.#latestDraft === null) {
        throw new ShiftDraftValueUnavailableError();
      }
      if (this.#inFlight === null) {
        this.#startWrite();
      }
      const inFlight = this.#inFlight;
      if (inFlight === null) {
        throw new ShiftDraftValueUnavailableError();
      }
      await inFlight;
    }
  }

  /**
   * Flushes the current generation, applies one external multi-store
   * transaction with the exact draft CAS token, then adopts the returned
   * DraftRecord. Ordinary autosave cannot race this operation.
   */
  public async persistAtomicDraftMutation<
    TResult extends { readonly draft: AppDraftRecord },
  >(input: {
    readonly expected_current_draft: ShiftEditorDraft;
    readonly next_draft: ShiftEditorDraft;
    readonly write: (draft: AppDraftSave) => Promise<TResult>;
  }): Promise<TResult> {
    this.#assertActive();
    if (this.#atomicWrite !== null) throw new ShiftDraftAtomicWriteInProgressError();
    const baseRecord = await this.saveNow();
    this.#assertActive();
    if (this.#atomicWrite !== null) throw new ShiftDraftAtomicWriteInProgressError();
    const currentDraft = this.#latestDraft;
    if (currentDraft === null) throw new ShiftDraftValueUnavailableError();
    const expectedCurrentDraft = parseShiftEditorDraft(input.expected_current_draft);
    if (!sameDraft(currentDraft, expectedCurrentDraft)) {
      throw new StoredShiftDraftPayloadError(
        'The current draft changed before its atomic mutation could start.',
      );
    }
    const nextDraft = parseShiftEditorDraft(input.next_draft);
    this.#cancelTimer();
    this.#publish('saving', null, null);

    const operation = (async () => {
      try {
        const result = await input.write({
          draft_key: this.#draftKey,
          payload: nextDraft,
          expected_draft_version: versionOf(baseRecord),
        });
        const loaded = parseStoredRecord(result.draft, this.#draftKey);
        if (
          loaded.record.draft_id !== baseRecord.draft_id ||
          loaded.record.autosave_revision !== baseRecord.autosave_revision + 1 ||
          !sameDraft(loaded.draft, nextDraft)
        ) {
          throw new StoredShiftDraftPayloadError(
            'Atomic draft mutation returned an unexpected durable draft version or payload.',
          );
        }
        if (!this.#disposed) {
          this.#latestDraft = loaded.draft;
          this.#expectedVersion = versionOf(loaded.record);
          this.#record = loaded.record;
          this.#editGeneration += 1;
          this.#persistedGeneration = this.#editGeneration;
          this.#publish('saved', null, null);
        }
        return result;
      } catch (error) {
        if (!this.#disposed) {
          if (error instanceof AppDraftConflictError) {
            this.#publish('conflict', null, error);
          } else {
            this.#publish('saved', null, null);
          }
        }
        throw error;
      }
    })();
    const atomicWrite = operation.then(
      () => undefined,
      () => undefined,
    );
    this.#atomicWrite = atomicWrite;
    try {
      return await operation;
    } finally {
      if (this.#atomicWrite === atomicWrite) this.#atomicWrite = null;
    }
  }

  /** Retry an ordinary failed write without weakening its previous CAS token. */
  public async retry(): Promise<AppDraftRecord> {
    this.#assertActive();
    if (this.#snapshot.status !== 'error') {
      return this.saveNow();
    }
    this.#publish('dirty', null, null);
    return this.saveNow();
  }

  /**
   * Explicitly discard the local conflicting value and load the durable one.
   * Invalid IndexedDB payloads are rejected rather than projected into the UI.
   */
  public async loadStoredVersion(): Promise<LoadedStoredShiftDraft | null> {
    this.#assertConflictResolution();
    let record: AppDraftRecord | null;
    try {
      record = await this.#storage.getDraft(this.#draftKey);
    } catch (error) {
      this.#publish('conflict', error, this.#snapshot.conflict);
      throw error;
    }
    this.#assertActive();

    if (record === null) {
      this.#latestDraft = null;
      this.#expectedVersion = null;
      this.#record = null;
      this.#markLoadedGeneration();
      this.#publish('clean', null, null);
      return null;
    }

    let loaded: LoadedStoredShiftDraft;
    try {
      loaded = parseStoredRecord(record, this.#draftKey);
    } catch (error) {
      this.#publish('conflict', error, this.#snapshot.conflict);
      throw error;
    }
    this.#latestDraft = loaded.draft;
    this.#expectedVersion = versionOf(loaded.record);
    this.#record = loaded.record;
    this.#markLoadedGeneration();
    this.#publish('clean', null, null);
    return loaded;
  }

  /**
   * Explicitly overwrite the current stored value with the local one.
   * A fresh read supplies the exact CAS token; a concurrent third write still
   * produces another conflict instead of becoming last-write-wins.
   */
  public async overwriteWithLatest(): Promise<AppDraftRecord> {
    this.#assertConflictResolution();
    if (this.#latestDraft === null) {
      throw new ShiftDraftValueUnavailableError();
    }

    let actualRecord: AppDraftRecord | null;
    try {
      actualRecord = await this.#storage.getDraft(this.#draftKey);
      if (actualRecord !== null) {
        parseStoredRecord(actualRecord, this.#draftKey);
      }
    } catch (error) {
      this.#publish('conflict', error, this.#snapshot.conflict);
      throw error;
    }
    this.#assertActive();

    this.#expectedVersion = actualRecord === null ? null : versionOf(actualRecord);
    this.#record = actualRecord;
    this.#publish('dirty', null, null);
    return this.saveNow();
  }

  /** Flush a pending debounce when focus leaves the page. */
  public handleBlur(): void {
    this.#requestImmediateWrite();
  }

  /** Flush only when the document is transitioning to hidden. */
  public handleVisibilityChange(hidden: boolean): void {
    if (hidden) {
      this.#requestImmediateWrite();
    }
  }

  /**
   * Cancel future work. An already-issued repository write cannot be revoked,
   * but its completion cannot notify listeners or start a coalesced successor.
   */
  public dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;
    this.#cancelTimer();
    this.#listeners.clear();
  }

  #assertActive(): void {
    if (this.#disposed) {
      throw new ShiftDraftAutosaveDisposedError();
    }
  }

  #assertConflictResolution(): void {
    this.#assertActive();
    if (this.#snapshot.status !== 'conflict' || this.#snapshot.conflict === null) {
      throw new TypeError('There is no draft conflict to resolve.');
    }
  }

  #markLoadedGeneration(): void {
    this.#editGeneration += 1;
    this.#persistedGeneration = this.#editGeneration;
  }

  #scheduleDebouncedWrite(): void {
    if (
      this.#disposed ||
      this.#timer !== null ||
      this.#snapshot.status === 'conflict' ||
      this.#snapshot.status === 'error' ||
      this.#persistedGeneration >= this.#editGeneration
    ) {
      return;
    }
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.#startWrite();
    }, this.#debounceMs);
  }

  #cancelTimer(): void {
    if (this.#timer !== null) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
  }

  #requestImmediateWrite(): void {
    if (this.#disposed || this.#snapshot.status === 'conflict' || this.#atomicWrite !== null) {
      return;
    }
    this.#cancelTimer();
    if (this.#snapshot.status !== 'error' && this.#inFlight === null) {
      this.#startWrite();
    }
  }

  #startWrite(): void {
    if (
      this.#disposed ||
      this.#inFlight !== null ||
      this.#atomicWrite !== null ||
      this.#latestDraft === null ||
      this.#snapshot.status === 'conflict' ||
      this.#snapshot.status === 'error' ||
      this.#persistedGeneration >= this.#editGeneration
    ) {
      return;
    }

    const generation = this.#editGeneration;
    const input: AppDraftSave = {
      draft_key: this.#draftKey,
      payload: this.#latestDraft,
      expected_draft_version: this.#expectedVersion,
    };
    this.#publish('saving', null, null);

    const inFlight = this.#performWrite(generation, input);
    this.#inFlight = inFlight;
    void inFlight.then(() => {
      if (this.#inFlight !== inFlight) {
        return;
      }
      this.#inFlight = null;
      if (
        !this.#disposed &&
        this.#snapshot.status === 'dirty' &&
        this.#persistedGeneration < this.#editGeneration
      ) {
        this.#startWrite();
      }
    });
  }

  async #performWrite(generation: number, input: AppDraftSave): Promise<void> {
    try {
      const rawRecord = await this.#storage.autosaveDraft(input);
      if (this.#disposed) {
        return;
      }
      const loaded = parseStoredRecord(rawRecord, this.#draftKey);
      this.#expectedVersion = versionOf(loaded.record);
      this.#record = loaded.record;
      this.#persistedGeneration = Math.max(this.#persistedGeneration, generation);
      this.#publish(
        this.#persistedGeneration >= this.#editGeneration ? 'saved' : 'dirty',
        null,
        null,
      );
    } catch (error) {
      if (this.#disposed) {
        return;
      }
      if (error instanceof AppDraftConflictError) {
        this.#publish('conflict', null, error);
        return;
      }
      this.#publish('error', error, null);
    }
  }

  #makeSnapshot(
    status: ShiftDraftAutosaveStatus,
    error: unknown | null,
    conflict: AppDraftConflictError | null,
  ): ShiftDraftAutosaveSnapshot {
    return {
      status,
      editGeneration: this.#editGeneration,
      persistedGeneration: this.#persistedGeneration,
      hasPendingChanges: this.#persistedGeneration < this.#editGeneration,
      record: this.#record,
      error,
      conflict,
    };
  }

  #publish(
    status: ShiftDraftAutosaveStatus,
    error: unknown | null,
    conflict: AppDraftConflictError | null,
  ): void {
    if (this.#disposed) {
      return;
    }
    this.#snapshot = this.#makeSnapshot(status, error, conflict);
    for (const listener of this.#listeners) {
      listener();
    }
  }
}

export type EditorSaveNow = () => Promise<unknown>;

/** Coordinates active editors before App asks AppStorage to drain writes. */
export class EditorSaveCoordinator {
  readonly #callbacks = new Set<EditorSaveNow>();

  public register(saveNow: EditorSaveNow): () => void {
    this.#callbacks.add(saveNow);
    return () => this.#callbacks.delete(saveNow);
  }

  public async saveNow(): Promise<void> {
    await Promise.all([...this.#callbacks].map(async (callback) => callback()));
  }
}

function versionOf(record: AppDraftRecord): AppDraftVersion {
  return {
    draft_id: record.draft_id,
    autosave_revision: record.autosave_revision,
  };
}

function sameDraft(left: ShiftEditorDraft, right: ShiftEditorDraft): boolean {
  return (
    canonicalJsonStringify(left satisfies JsonValue) ===
    canonicalJsonStringify(right satisfies JsonValue)
  );
}

function parseStoredRecord(
  record: AppDraftRecord,
  expectedDraftKey: string,
): LoadedStoredShiftDraft {
  if (record.draft_key !== expectedDraftKey || record.draft_kind !== 'shift_editor') {
    throw new StoredShiftDraftPayloadError(
      `Stored draft does not match editor key ${expectedDraftKey}.`,
    );
  }
  try {
    return { draft: parseShiftEditorDraft(record.payload), record };
  } catch (error) {
    throw new StoredShiftDraftPayloadError(
      `Stored draft ${expectedDraftKey} has an invalid shift editor payload.`,
      error,
    );
  }
}
